import React, { useState } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux"
import { useInterval } from "../timing/use-interval"
import {
  fetchTimelapses,
  selectTimelapsesByContext,
  selectTimelapsesById,
  selectTimelapsesByIds,
} from "./slice"
import LoadingSpinnerInline from "../../ui/LoadingSpinnerInline"
import Button from "../../ui/Button"

import Timestamp from "../../ui/Timestamp"
import { selectAuth } from "../iam/slice"
import { timelapseMediaSrc } from "./api"
import {
  faBackwardFast,
  faForwardFast,
} from "@fortawesome/free-solid-svg-icons"
import { selectAdvancedMode } from "../settings/slice"

const UPDATE_INTERVAL = 300000 // ms - 5mins

const Container = styled.div`
  background: black;
  position: relative;
  display: flex;
  flex-direction: column;
  flex: auto;
  align-items: stretch;
  justify-content: center;
  border-radius: 0.5rem;
`

const VideoBox = styled.div`
  flex: auto;
  width: 100%;
  height: 100%;
  position: relative;
`

const Video = styled.video`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const NavControls = styled.div`
  flex: none;
  display: flex;
  align-items: stretch;
  padding: 0.5rem;
`

const CurrentlyPlaying = styled.div`
  font-size: 0.9rem;
  flex: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.25rem;
`

/* Minimalist video player that fetches all the recent all-sky timelapse videos
    metadata, and allows the user to play/pause (looping infinitely) as well as
    navigate to older or newer videos.
    Design note: 
    All-sky videos are square, with only the center circular
    are of interest (fish-eye lens). This component exploits that characteristic
    by placing controls in the corners to maximise useful display space. 
*/
const AllSkyVideo = ({ astrosite_id }) => {
  // Index of the video that should currently be displayed.
  const [selIndex, setSelIndex] = useState(0)

  // Data from global app state:
  const context = `astrosite/${astrosite_id}`
  // IDs of all videos that user can navigate to
  const metaIDs = useSelector((state) =>
    selectTimelapsesByContext(state, context)
  )
  // Data for currently-selected video
  const meta = useSelector((state) =>
    (metaIDs?.ids || []).length > selIndex
      ? selectTimelapsesById(state)[metaIDs?.ids[selIndex]]
      : null
  )
  // Actual metadatas
  const metas = useSelector((state) =>
    selectTimelapsesByIds(state, metaIDs?.ids || [])
  )

  // Data retrieval:
  // Effect: Fetch all videos metadata - initially, and polling
  const dispatch = useDispatch()
  useInterval(
    async () => {
      const d = await dispatch(
        fetchTimelapses({
          filter: {
            astrosite: astrosite_id,
            sort_by: "start_time",
            sort_order: "DESC",
          },
          page: {
            offset: 0,
            limit: 24, // Roughly the last 24 hours, videos are currently hourly
          },
          context,
        })
      )
      // If user deliberately selected a video previously, try to keep it
      // selected, otherwise latest becomes selected.
      if (
        Array.isArray(d?.payload?.data) &&
        selIndex > 0 &&
        selIndex + 1 < d?.payload?.data.length
      ) {
        setSelIndex(selIndex + 1)
      }
    },
    UPDATE_INTERVAL,
    true
  )

  // Actions: Navigation between videos
  const toNewer = () => setSelIndex(Math.max(0, selIndex - 1))
  const toOlder = () =>
    setSelIndex(Math.min((metas || []).length - 1, selIndex + 1))

  const { token } = useSelector(selectAuth)
  const advancedMode = useSelector(selectAdvancedMode)

  return (
    <Container>
      {!meta && metaIDs?._loadStatus == "loading" && <LoadingSpinnerInline />}
      {meta && metas && (
        <>
          <VideoBox>
            <Video
              src={timelapseMediaSrc(meta.id, token)}
              controls
              loop
              preload="auto"
              autoplay
            />
          </VideoBox>
          <NavControls>
            <Button
              icon={faBackwardFast}
              onClick={toOlder}
              disabled={selIndex + 1 >= metas?.length}
              title="Previous (older)"
            >
              Older
            </Button>
            <CurrentlyPlaying>
              <Timestamp utc={advancedMode}>{meta.start_time}</Timestamp>
              <span>&nbsp;-&nbsp;</span>
              <Timestamp utc={advancedMode}>{meta.end_time}</Timestamp>
            </CurrentlyPlaying>
            <Button
              icon={faForwardFast}
              onClick={toNewer}
              disabled={selIndex == 0}
              title="Next (newer)"
            >
              Newer
            </Button>
          </NavControls>
        </>
      )}
    </Container>
  )
}

AllSkyVideo.propTypes = {
  astrosite_id: PropTypes.string.isRequired,
}

export default AllSkyVideo
